import globalLogger from "@lazarentertainment/js-logger"
import { captureException } from "@sentry/nextjs"
import { useChannelContext } from "client/components/channel/ChannelContext"
import {
  useOrderCreatedForCartSubscription,
  useTrackCartUpdateMutation,
  useViewerSignalledReturnToShowSubscription,
} from "client/graphql/commerce-api"
import {
  CartLineInput,
  GetCartWithLinesQuery,
  useAddCartItemMutation,
  useCreateCartMutation,
  useGetCartWithLinesQuery,
  useRemoveCartItemMutation,
  useUpdateCartAttributesMutation,
  useUpdateCartDiscountCodesMutation,
  useUpdateCartItemMutation,
} from "client/graphql/storefront-api"
import { debounce } from "lodash"
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { usePreviousDifferent } from "rooks"
import { useIsWindowFocused } from "shared/hooks/useIsWindowFocused"
import { Publish, Subscribe, usePubSub } from "shared/hooks/usePubSub"
import { openUrlPopup } from "shared/utils/openUrlPopup"

import { getCartDiff } from "../../utils/getCartDiff"
import type { ProductVariant } from "../../utils/getDefaultVariantForProduct"
import { useShopContext } from "../ShopContext"

export type CartError = string

export type CartData = NonNullable<GetCartWithLinesQuery["cart"]>
type CartState = {
  cart?: CartData | null
  isCartLoading: boolean
  onCheckoutClick: () => void
  onCartEvent: Subscribe<CartEvent>
  onBuyNowClick: (variantId: string, quantity: number) => void
}

const CartContext = createContext<CartState>({
  cart: null,
  isCartLoading: false,
  onCheckoutClick: () => {},
  onCartEvent: () => () => {},
  onBuyNowClick: () => {},
})

type CartMutationState = {
  incrementMutationCount: () => void
  decrementMutationCount: () => void
  publishCartEvent: Publish<CartEvent>
}

const PrivateCartContext = createContext<CartMutationState>({
  incrementMutationCount: () => {},
  decrementMutationCount: () => {},
  publishCartEvent: () => {},
})

export type CartLineItem = NonNullable<
  GetCartWithLinesQuery["cart"]
>["lines"]["nodes"][number]

type CartProviderProps = {
  children?: ReactNode
}

const getCartLocalStorageKey = (
  shopId: string,
  recordingId: string | null,
  channelId: string,
) => `s10.bv.shopify_cart_id--${shopId}--${recordingId ?? channelId}`

function logAndCaptureError(error: unknown) {
  globalLogger.error(error)
  captureException(error)
}

type CartEvent = {
  "return-to-show": never
  error: { message: string }
}

export function CartProvider({ children }: CartProviderProps) {
  const { publish, subscribe } = usePubSub<CartEvent>()
  const isWindowFocused = useIsWindowFocused()
  const { shop, isStorefrontLinkReady, resetView } = useShopContext()
  const discountCode = shop?.activeSale?.discountCode
  const saleId = shop?.activeSale?.id
  const { recordingId, channelId } = useChannelContext()
  const cartLocalStorageKey = shop?.id
    ? getCartLocalStorageKey(shop.id, recordingId, channelId)
    : null

  const setLocalStorageCartId = (id: string | null | undefined) => {
    if (typeof window === "undefined" || !cartLocalStorageKey) {
      return
    }
    if (id) {
      localStorage.setItem(cartLocalStorageKey, id)
    } else {
      localStorage.removeItem(cartLocalStorageKey)
    }
  }

  // This Cart handles the user adding items to their cart. Separately, we
  // may create temporary carts for Buy Now purchases.
  const [persistentCartId, setPersistentCartId] = useState<string | null>(
    null,
  )
  const updateCartId = (
    data:
      | {
          cart?: { id: string } | null
        }
      | null
      | undefined,
  ) => {
    const id = data?.cart?.id ?? null
    setPersistentCartId(id)
    setLocalStorageCartId(id)
  }

  const [updateCartDiscountCodes] = useUpdateCartDiscountCodesMutation({
    onError: logAndCaptureError,
  })
  const [updateCartAttributesMutation] = useUpdateCartAttributesMutation({
    onError: logAndCaptureError,
  })
  const [createCartMutation, { loading: isCreatingCart }] =
    useCreateCartMutation({
      onError: logAndCaptureError,
    })
  const createCart = async (lines?: CartLineInput[]) => {
    try {
      if (!saleId) {
        throw new Error("A saleId is needed to create a Cart")
      }

      const { data } = await createCartMutation({
        variables: {
          input: {
            attributes: [{ key: "saleId", value: saleId }],
            discountCodes: discountCode ? [discountCode] : undefined,
            lines,
          },
        },
        context: { useStorefrontAPI: true },
        onError: logAndCaptureError,
      })

      const cart = data?.cartCreate?.cart

      if (cart?.id) {
        await updateCartAttributesMutation({
          variables: {
            cartId: cart.id,
            attributes: [
              { key: "saleId", value: saleId },
              { key: "cartId", value: cart.id },
            ],
          },
          context: { useStorefrontAPI: true },
        })
      }

      return cart ?? null
    } catch (error) {
      logAndCaptureError(error)
      return null
    }
  }

  const {
    data: getCartData,
    loading: isCartLoading,
    refetch,
  } = useGetCartWithLinesQuery({
    skip: !persistentCartId || !isStorefrontLinkReady,
    variables: { cartId: persistentCartId ?? "" },
    context: { useStorefrontAPI: true },
    onCompleted: updateCartId,
    onError: logAndCaptureError,
  })

  const persistentCart = getCartData?.cart ?? null
  const checkoutWindowsRef = useRef<Map<string, Window>>()
  if (!checkoutWindowsRef.current) {
    checkoutWindowsRef.current = new Map()
  }
  const checkoutWindows = checkoutWindowsRef.current

  const onCheckoutClick = useCallback(() => {
    if (!persistentCart) {
      logAndCaptureError(new Error("Cart is required but wasn't set"))
      return
    }
    const popupWindow = openUrlPopup(persistentCart.checkoutUrl)
    if (popupWindow) {
      checkoutWindows.set(persistentCart.id, popupWindow)
    }
  }, [persistentCart])

  const previousCartId = usePreviousDifferent(persistentCart?.id)
  useEffect(() => {
    // Switches back to the PRODUCT view after a checkout is completed.
    if (previousCartId && !persistentCart?.id) {
      resetView()
    }
  }, [previousCartId, persistentCart?.id])

  // The user may keep checkout windows open for some reason. Or, they may
  // switch focus from the checkout window to the player window, after checkout,
  // without closing the checkout window.
  // (See https://github.com/lazarentertainment/broadcast-viewer/issues/2358)
  // In order for the buttons in those checkout windows to work, we need to
  // retain subscriptions to those cart ids.
  const [subscribedCartIds, setSubscribedCartIds] = useState<string[]>([])
  useEffect(() => {
    if (persistentCartId) {
      setSubscribedCartIds([...subscribedCartIds, persistentCartId])
    }
  }, [persistentCartId])

  const onBuyNowClick = useCallback(
    (variantId: string, quantity: number) => {
      const popupWindow = openUrlPopup("")
      createCart([{ quantity, merchandiseId: variantId }]).then(
        (temporaryCart) => {
          if (temporaryCart && popupWindow) {
            if (temporaryCart.totalQuantity < 1) {
              // This can happen if the item is not available to the buyer for some reason.
              popupWindow.close()
              publish("error", {
                message: "Sorry, this item is not available.",
              })
              return
            }
            // Note carefully... TODO is the list of checkout window cart ids the same
            // as the list of subscribed cart ids?
            checkoutWindows.set(temporaryCart.id, popupWindow)
            setSubscribedCartIds([...subscribedCartIds, temporaryCart.id])
            popupWindow.location.href = temporaryCart.checkoutUrl
          } else {
            // If we managed to open a popup window but failed to create the cart, just
            // close the popup window. We can't proceed.
            logAndCaptureError(
              new Error("Buy Now failed to create a cart"),
            )
            popupWindow?.close()
          }
        },
      )
    },
    [subscribedCartIds, saleId, discountCode],
  )

  useOrderCreatedForCartSubscription({
    skip: !subscribedCartIds.length,
    variables: { cartIds: subscribedCartIds },
    onSubscriptionData: ({ subscriptionData, client }) => {
      const orderCreatedCartId =
        subscriptionData.data?.orderCreatedForCart?.cartId
      if (persistentCart && persistentCart.id === orderCreatedCartId) {
        // If an order was created, we know this Cart has been deleted.
        updateCartId(null)
        const isEvicted = client.cache.evict({
          id: client.cache.identify({
            __typename: "Cart",
            id: persistentCart.id,
          }),
        })
        if (isEvicted) {
          client.cache.gc()
        }
      }
    },
  })

  useViewerSignalledReturnToShowSubscription({
    skip: !subscribedCartIds.length,
    variables: { ids: subscribedCartIds },
    onSubscriptionData: ({ subscriptionData }) => {
      const signalId =
        subscriptionData.data?.viewerSignalledReturnToShow?.id
      if (signalId) {
        setSubscribedCartIds(
          subscribedCartIds.filter((id) => id !== signalId),
        )
        checkoutWindows.get(signalId)?.close()
        checkoutWindows.delete(signalId)
      }
      publish("return-to-show")
    },
    onError: logAndCaptureError,
  })

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !isStorefrontLinkReady ||
      !cartLocalStorageKey ||
      persistentCartId ||
      isCreatingCart
    ) {
      return
    }

    // Note: viewers are allowed to checkout an existing Cart,
    // even if there is no active sale.
    const storedCartId = localStorage.getItem(cartLocalStorageKey)
    if (storedCartId) {
      setPersistentCartId(storedCartId)
    } else if (saleId) {
      createCart().then((cart) => updateCartId({ cart }))
    }
  }, [
    isStorefrontLinkReady,
    isCreatingCart,
    cartLocalStorageKey,
    persistentCartId,
    saleId,
  ])

  // There appears to be a race condition in the Storefront API when
  // setting the discount and attributes at the same time. So, this function
  // waits for the discount code to be set before setting the attributes.
  async function setDiscountAndAttributes() {
    if (!persistentCart || !saleId) {
      return
    }

    const isDiscountSet =
      !discountCode ||
      persistentCart.discountCodes.some(
        ({ code }) => code === discountCode,
      )

    if (!isDiscountSet) {
      await updateCartDiscountCodes({
        variables: {
          cartId: persistentCart.id,
          discountCodes: [discountCode],
        },
        context: { useStorefrontAPI: true },
      })
    }

    const isAttributesSet =
      persistentCart.attributes.some(({ key }) => key === "saleId") &&
      persistentCart.attributes.some(({ key }) => key === "cartId")

    if (!isAttributesSet) {
      await updateCartAttributesMutation({
        variables: {
          cartId: persistentCart.id,
          attributes: [
            { key: "saleId", value: saleId },
            { key: "cartId", value: persistentCart.id },
          ],
        },
        context: { useStorefrontAPI: true },
      })
    }
  }

  useEffect(() => {
    setDiscountAndAttributes().catch(logAndCaptureError)
  }, [persistentCart?.id, saleId, discountCode])

  useEffect(() => {
    if (isWindowFocused && isStorefrontLinkReady && persistentCart?.id) {
      refetch()
        .then(({ data }) => updateCartId(data))
        .catch(logAndCaptureError)
    }
  }, [isWindowFocused, isStorefrontLinkReady])

  const [trackCartUpdate] = useTrackCartUpdateMutation({
    optimisticResponse: { trackCartUpdate: true },
    onError: (error) => {
      globalLogger.error(
        "An error occurred while tracking cart update",
        error.message,
      )
    },
  })

  // This ref is needed in order to determine the state of the cart since the
  // previous `handleCartUpdate` call.
  const prevCartContentsRef = useRef<CartLineItem[]>([])

  // A debounced function to publish analytics events when changes to cart
  // contents are detected.
  const handleCartUpdate = useMemo(
    () =>
      debounce((currentItems: CartLineItem[]) => {
        if (
          !currentItems ||
          !prevCartContentsRef.current ||
          !shop ||
          !saleId
        ) {
          return
        }
        const cartDiff = getCartDiff(
          currentItems,
          prevCartContentsRef.current,
        )

        cartDiff.forEach((itemDiff) => {
          if (itemDiff.quantity) {
            trackCartUpdate({
              variables: {
                shopId: shop.id,
                saleId,
                productId: itemDiff.merchandise.product.id,
                variantId: itemDiff.merchandise.id,
                quantity: itemDiff.quantity,
              },
            })
          }
        })

        prevCartContentsRef.current = currentItems
      }, 1000),
    [shop],
  )

  useEffect(() => {
    if (persistentCart) {
      handleCartUpdate(persistentCart.lines.nodes)
    }
  }, [persistentCart?.lines.nodes])

  // Track the number of mutations in flight, so that we can have
  // line-item-level loading states, while the CartContext can
  // indicate if any mutation is ongoing.
  const [mutationCount, setMutationCount] = useState(0)
  const incrementMutationCount = () => setMutationCount(mutationCount + 1)
  const decrementMutationCount = () =>
    setMutationCount(Math.max(0, mutationCount - 1))

  return (
    <CartContext.Provider
      value={{
        cart: persistentCart,
        isCartLoading: isCartLoading || mutationCount > 0,
        onCheckoutClick,
        onCartEvent: subscribe,
        onBuyNowClick,
      }}
    >
      <PrivateCartContext.Provider
        value={{
          publishCartEvent: publish,
          incrementMutationCount,
          decrementMutationCount,
        }}
      >
        {children}
      </PrivateCartContext.Provider>
    </CartContext.Provider>
  )
}

export const useCartContext = () => {
  const cartContext = useContext(CartContext)
  if (!cartContext) {
    throw new Error(
      "No CartContext.Provider found when calling useCartContext.",
    )
  }
  return cartContext
}

export function useShopifyCart() {
  const { cart } = useCartContext()

  const {
    incrementMutationCount,
    decrementMutationCount,
    publishCartEvent,
  } = useContext(PrivateCartContext)
  async function mutate<ResultType>(mutation: () => Promise<ResultType>) {
    incrementMutationCount()
    try {
      const result = await mutation()
      return result
    } finally {
      decrementMutationCount()
    }
  }

  const onCartError = () => {
    publishCartEvent("error", {
      message: "Something went wrong updating your cart.",
    })
  }

  const publishCartUserErrorIfExists = (
    userErrors?: Array<{ message: string }> | null,
  ) => {
    if (userErrors?.[0]) {
      publishCartEvent("error", {
        message: userErrors[0].message,
      })
    }
  }

  const [addCartItem, { loading: isAddingItem }] = useAddCartItemMutation({
    onError: onCartError,
    onCompleted: (data) => {
      publishCartUserErrorIfExists(data.cartLinesAdd?.userErrors)
    },
  })
  const [removeCartItem, { loading: isRemovingItem }] =
    useRemoveCartItemMutation({
      onError: onCartError,
      onCompleted: (data) => {
        publishCartUserErrorIfExists(data.cartLinesRemove?.userErrors)
      },
    })
  const [updateCartItem, { loading: isUpdatingItem }] =
    useUpdateCartItemMutation({
      onError: onCartError,
      onCompleted: (data) => {
        publishCartUserErrorIfExists(data.cartLinesUpdate?.userErrors)
      },
    })

  const getLineItem = (lineId: string) => {
    return cart?.lines.nodes.find((node) => node.id === lineId) ?? null
  }

  const addItem = (variant: ProductVariant, quantity = 1) =>
    mutate(async () => {
      if (!cart) {
        throw new Error("Can't addItem without a cart")
      }

      if (quantity < 1) {
        throw new Error("Quantity cannot be less than 1")
      }

      return addCartItem({
        variables: {
          cartId: cart.id,
          lines: {
            merchandiseId: variant.id,
            quantity,
          },
        },
        context: { useStorefrontAPI: true },
        optimisticResponse: {
          cartLinesAdd: {
            __typename: "CartLinesAddPayload",
            userErrors: [],
            cart: {
              ...cart,
              totalQuantity: cart.totalQuantity + quantity,
              lines: {
                ...cart.lines,
                nodes: [
                  ...cart.lines.nodes,
                  {
                    __typename: "CartLine",
                    id: "optimistic-cart-line-item",
                    quantity,
                    // Can't calculate discounts optimistically
                    discountAllocations: [],
                    merchandise: variant,
                    cost: {
                      amountPerQuantity: {
                        amount: variant.price.amount,
                        currencyCode: variant.price.currencyCode,
                      },
                      subtotalAmount: {
                        amount: variant.price.amount * quantity,
                        currencyCode: variant.price.currencyCode,
                      },
                      totalAmount: {
                        amount: variant.price.amount * quantity,
                        currencyCode: variant.price.currencyCode,
                      },
                    },
                  },
                ],
              },
            },
          },
        },
      })
    })

  const removeItem = (lineId: string) =>
    mutate(async () => {
      if (!cart) {
        throw new Error("Can't removeItem without a cart")
      }

      return removeCartItem({
        variables: {
          cartId: cart.id,
          lineIds: lineId,
        },
        context: { useStorefrontAPI: true },
        optimisticResponse: {
          cartLinesRemove: {
            __typename: "CartLinesRemovePayload",
            userErrors: [],
            cart: {
              ...cart,
              totalQuantity:
                cart.totalQuantity - (getLineItem(lineId)?.quantity ?? 0),
              lines: {
                ...cart.lines,
                nodes: cart.lines.nodes.filter(
                  (node) => node.id !== lineId,
                ),
              },
            },
          },
        },
      })
    })

  const changeQuantity = (lineId: string, change: number) =>
    mutate(async () => {
      if (!cart) {
        throw new Error("Can't changeQuantity without a cart")
      }

      const lineItem = getLineItem(lineId)
      if (!lineItem) {
        throw new Error("Line item not found")
      }

      const newQuantity = lineItem.quantity + change

      if (newQuantity < 1) {
        throw new Error("Quantity cannot be less than 1")
      }

      return updateCartItem({
        variables: {
          cartId: cart.id,
          lines: { id: lineItem.id, quantity: newQuantity },
        },
        context: { useStorefrontAPI: true },
        optimisticResponse: {
          cartLinesUpdate: {
            __typename: "CartLinesUpdatePayload",
            userErrors: [],
            cart: {
              ...cart,
              totalQuantity: cart.totalQuantity + change,
              lines: {
                ...cart.lines,
                nodes: cart.lines.nodes.map((node) => {
                  if (node.id !== lineItem.id) {
                    return node
                  }

                  return {
                    ...node,
                    quantity: newQuantity,
                    cost: {
                      ...node.cost,
                      // Note, we can't calculate discounts optimistically,
                      // so `totalAmount` does not get updated immediately.
                      subtotalAmount: {
                        ...node.cost.subtotalAmount,
                        amount:
                          node.cost.amountPerQuantity.amount * newQuantity,
                      },
                    },
                  }
                }),
              },
            },
          },
        },
      })
    })

  const increaseQuantity = (lineId: string) => changeQuantity(lineId, 1)
  const decreaseQuantity = (lineId: string) => changeQuantity(lineId, -1)

  return {
    addItem,
    removeItem,
    changeQuantity,
    increaseQuantity,
    decreaseQuantity,
    isLoading: isAddingItem || isRemovingItem || isUpdatingItem,
  }
}
